// The AuthSession DTO — Shape A, as Connect locked it 2026-10-19.
//
// Authority: /ceph/projects/coordination/sdk-demos/SDK-DEMO-LOGIN-STANDARD-RECIPE.md §2.
//
// ---------------------------------------------------------------------------
// WHY THESE TYPES ARE DECLARED HERE RATHER THAN IMPORTED FROM @anshinus/auth
// ---------------------------------------------------------------------------
// `@anshinus/auth` exports an `AuthSession` type, but its own doc comment calls it the
// "narrow shape (identity + expiry only)" and says it "DELIBERATELY does not carry
// `permissions` or `modules`", because "reading empty defaults for authorization is a
// silent-failure vector". The recipe's Shape A is the shape of the two WIRE RESPONSES
// (`/session/validate` and `/me`), which is a different thing from the provider's
// narrowed context value.
//
// So these are the wire contracts, named after the endpoints, and the mapping below is
// the only place the two are combined. Importing the provider's narrowed type and
// pretending it was the wire shape is how a `tenant_id` that does not exist gets read as
// `undefined` and then defaulted.
//
// ---------------------------------------------------------------------------
// THE THREE SHAPE-A RULES THIS FILE EXISTS TO ENFORCE
// ---------------------------------------------------------------------------
// 1. `tenant` IS NESTED. `me.tenant.tenant_id`. There is NO top-level `me.tenant_id`.
//    A reader that reaches for the top-level field gets `undefined`, and anything that
//    then substitutes a default is asserting a tenant the server never named.
// 2. `is_platform_admin` IS NOT IN `/me`. It is only on `/session/validate`'s `user{}`.
//    Reading it from `/me` yields `undefined`, which is falsy — so a platform admin
//    silently looks like a normal user, and the failure is invisible because the common
//    case is already `false`.
// 3. CAPABILITY DERIVES FROM `roles[]`, not from `is_platform_admin`.

/** `GET /api/auth/session/validate` — the wire shape. */
export interface SessionValidateDto {
  revoked: boolean;
  expires_at: string;
  user: {
    user_id: string;
    email: string;
    display_name?: string;
    /** ONLY here. Absent from /me — see rule 2. */
    is_platform_admin?: boolean;
  };
  roles?: string[];
}

/** `GET /api/auth/me` — the wire shape. */
export interface MeDto {
  session_id: string;
  user: {
    user_id: string;
    email: string;
    display_name?: string;
  };
  roles?: string[];
  permissions?: Record<string, { read?: boolean; create?: boolean; update?: boolean; delete?: boolean }>;
  modules?: string[];
  /** NESTED — see rule 1. */
  tenant?: { tenant_id: string; key?: string; name?: string };
  tenants?: Array<{ tenant_id: string; key?: string; name?: string }>;
}

/**
 * The resolved identity the demo runs AS. Assembled from both endpoints.
 *
 * Every field is non-optional except `displayName` and `isPlatformAdmin`, so a consumer
 * cannot accidentally render a half-resolved session. `tenantId` in particular is
 * REQUIRED: a session with no tenant is not a session this demo can act under, because
 * the launcher's `tenant` query param would then have to be invented.
 */
export interface ResolvedSession {
  /** `sub` — the Connect user id. */
  sub: string;
  email: string;
  displayName: string | null;
  /** From `me.tenant.tenant_id`. Never defaulted, never invented. */
  tenantId: string;
  /** From `/session/validate.user.is_platform_admin` ONLY. */
  isPlatformAdmin: boolean;
  /** Union of both endpoints' roles, deduplicated and sorted. Capability derives here. */
  roles: string[];
  sessionId: string;
  expiresAt: string;
}

/** Why a pair of DTOs could not be resolved into a session. Rendered to the tester. */
export class SessionShapeError extends Error {
  constructor(public readonly field: string, detail: string) {
    super(`AuthSession Shape A: ${field} — ${detail}`);
    this.name = 'SessionShapeError';
  }
}

/**
 * Combine `/session/validate` and `/me` into the identity the demo acts as.
 *
 * THROWS rather than returning a partial session. The caller treats a throw as
 * "not authenticated" and shows the login or held state — it must never proceed with a
 * session whose tenant or subject could not be read, because the next thing that happens
 * is a launch URL carrying a `tenant` query param, and an invented tenant in that param
 * is a fabricated scope claim.
 *
 * REVOKED IS CHECKED FIRST. A revoked session still returns a populated `user{}`, so a
 * mapper that read the fields before the flag would resolve a perfectly well-formed
 * identity for a session Connect has explicitly killed.
 */
export function resolveSession(validate: SessionValidateDto, me: MeDto): ResolvedSession {
  if (validate.revoked) {
    throw new SessionShapeError('revoked', 'Connect reports this session as revoked');
  }

  const sub = validate.user?.user_id;
  if (typeof sub !== 'string' || sub === '') {
    throw new SessionShapeError('user.user_id', 'missing from /session/validate');
  }

  // Rule 1. Read ONLY the nested field. `me.tenant_id` is not part of Shape A, and a
  // fallback to it would quietly accept a non-conforming server — then stop working the
  // day Connect stops sending the field nobody was supposed to be reading.
  const tenantId = me.tenant?.tenant_id;
  if (typeof tenantId !== 'string' || tenantId === '') {
    throw new SessionShapeError(
      'me.tenant.tenant_id',
      'missing or empty; Shape A has no top-level tenant_id to fall back to, and this demo will not invent a tenant',
    );
  }

  // The two endpoints must agree on WHO this is. They are separate calls, so a cookie
  // that changed between them (logout-and-login in another tab, a rotated session) would
  // otherwise produce a session whose subject came from one user and whose tenant and
  // permissions came from another.
  if (me.user?.user_id && me.user.user_id !== sub) {
    throw new SessionShapeError(
      'user.user_id',
      `/session/validate and /me disagree (${sub} vs ${me.user.user_id}); the cookie changed mid-handshake`,
    );
  }

  // `firstNonBlank`, NOT `??`. `??` falls through only on null/undefined, so
  // `validate.user.email ?? me.user?.email` returns the EMPTY STRING when validate
  // carries `email: ""` and never consults /me. This is the `${VAR:-default}` trap in
  // TypeScript form — a defaulting operator that does not fire for the blank value the
  // caller actually meant to be absent. Caught by `dto.test.ts`'s fallback test, which
  // failed against the `??` version.
  const email = firstNonBlank(validate.user.email, me.user?.email);
  if (email === null) {
    throw new SessionShapeError('user.email', 'missing from both endpoints');
  }

  return {
    sub,
    email,
    displayName: firstNonBlank(validate.user.display_name, me.user?.display_name),
    tenantId,
    // Rule 2. From validate ONLY. Deliberately NOT `?? me.is_platform_admin` — that
    // field does not exist, and reading it would always yield undefined -> false, which
    // is indistinguishable from a correct answer for every non-admin.
    isPlatformAdmin: validate.user.is_platform_admin === true,
    roles: mergeRoles(validate.roles, me.roles),
    sessionId: me.session_id,
    expiresAt: validate.expires_at,
  };
}

/**
 * Union of the two endpoints' role lists, deduplicated and sorted.
 *
 * Sorted so a capability check and a rendered role list are stable across calls; a
 * union rather than a preference because the recipe names `roles[]` on BOTH responses
 * and does not say one supersedes the other. Taking only one would silently drop a role
 * the other carried, and a dropped role is a capability the user has and cannot use.
 */
/**
 * First of the candidates that is a non-blank string, else null.
 *
 * Exists because `??` treats `""` as present. A blank field from Connect means absent,
 * and the difference decides whether the fallback endpoint is consulted at all.
 */
function firstNonBlank(...candidates: Array<string | undefined | null>): string | null {
  for (const c of candidates) {
    if (typeof c === 'string' && c.trim() !== '') return c;
  }
  return null;
}

function mergeRoles(a: string[] | undefined, b: string[] | undefined): string[] {
  const out = new Set<string>();
  for (const list of [a, b]) {
    if (!Array.isArray(list)) continue;
    for (const r of list) if (typeof r === 'string' && r !== '') out.add(r);
  }
  return [...out].sort();
}

/**
 * Does this session hold the named role?
 *
 * EXACT match on the whole string. Not `includes`, not a prefix: `scheduler-admin`
 * must not be satisfied by `scheduler-admin-readonly`, and `admin` must not be satisfied
 * by any role that merely contains it.
 */
export function hasRole(session: ResolvedSession, role: string): boolean {
  return session.roles.includes(role);
}

// NOTE: the single-role launcher gate that used to live here (`LAUNCHER_ROLE` +
// `hasLauncherCapability`) has been REPLACED by the three-tier resolver in
// `./capabilities.ts`. It is deleted rather than kept as a convenience wrapper: two
// functions that both answer "may this session act?" are two places for the answer to
// drift, and the one nobody updates is the one still wired to a button.
